
import type {Color} from "./colorObject";
import {ResourceInvalidationTriggers} from "../global/resourcemanager/resourceManager";
import {ResourceManagerTaskPrerequisite} from "../global/resourcemanager/task";
import type {PalettenEintragFromDB} from "./databaseInterfaceClasses";
import globalsObject, {handleError} from "./globalsObject";
import {getUniqueProperties} from "../global/arrayHelper";
import {ChromoLogoColor} from "@k/js/selectableObjects";
import {usePalettenClusterStore} from "@k/vue/store/palettenClusterStore";

export class PalettenLoader {

    static getpalettenClusterToLoad(colorObjects : Color[]|ChromoLogoColor[]) {
        return getUniqueProperties(colorObjects, (c:Color)=>c.palettenCluster);
    }

    static getPalettenToLoad(colorObjects : Color[]):string[] {
        return getUniqueProperties(colorObjects, (c:Color)=>c.palette);
    }

    public static getPalettenClusterTaskPrerequisites(palettenClusterToLoad:string[]):ResourceManagerTaskPrerequisite[] {
        return palettenClusterToLoad.map(name=>new ResourceManagerTaskPrerequisite("palettenCluster_"+name, ["loaded"]));
    }

    static loadPalettenCluster (palettenClusterNames : string[]) {
        const clustersToLoad = palettenClusterNames.filter(function(name) {
                return globalsObject.resourceManager.getResourceByName(PalettenLoader.getPalettenClusterResourceName(name)) === null;
            }),
            resourceName = "palettenClusterLoading_" + clustersToLoad.join();
        if (clustersToLoad.length === 0) {
            return;
        }

        for (let i = 0, lenI = clustersToLoad.length; i < lenI; i++) {
            globalsObject.resourceManager.registerResource({
                resourceName: "palettenCluster_" + clustersToLoad[i],
                startingStati: ["loading"]
            }).addMultipleInvalidationTriggers([ResourceInvalidationTriggers.USERCHANGED]);
        }

        globalsObject.resourceManager.registerResource({
            resourceName: resourceName,
            failHandler: function () {
                handleError("ajaxFail", "registerResource", "could not load palettes!");
            },
            invalidatedWhen: "paletteClustersAreProcessed_" + clustersToLoad.join()
        }).load({
            data: {palettenClusters: JSON.stringify(clustersToLoad), sport: globalsObject.controller.model.getCurrentOwayoProduct().realsport},
            url: "/konfigurator_php/designmodul/colors/loadPalettenCluster.php",
            method: "POST"
        });

        globalsObject.resourceManager.createTask("processPalettes" + clustersToLoad.join())
            .withContext(this)
            .invalidAfter(ResourceInvalidationTriggers.USERCHANGED)
            .discardAfterExecution()
            .when(resourceName, "loaded")
            .thenDo(
                function (resourceData) {
                    PalettenLoader.processPalettenClusters(resourceData, resourceName);
                }
            );
    }

    private static registerResourceForPalette(palette) {
        globalsObject.resourceManager.registerResource({
            resourceName: "palette_" + palette,
            startingStati: ["loading"]
        }).addMultipleInvalidationTriggers([ResourceInvalidationTriggers.USERCHANGED]);
    }

    private static processPalettenClusters (resourceData, resourceName) {
        const palettenCluster = resourceData[resourceName];
        for (const name in palettenCluster) {
            PalettenLoader.processSinglePalettenCluster(name, palettenCluster[name]);
        }
        globalsObject.resourceManager.executeInvalidation(resourceName.replace("palettenClusterLoading_", "paletteClustersAreProcessed_"));
    }

    private static processSinglePalettenCluster(clustername, palettenCluster) {
        const palettenCLusterStore = usePalettenClusterStore();
        palettenCLusterStore.setPalettenCluster(clustername, palettenCluster);
        globalsObject.resourceManager.publishResource("palettenCluster_"+clustername, palettenCluster);
        globalsObject.resourceManager.appendStatus("palettenCluster_"+clustername, "loaded");

        for(const palette in palettenCluster) {
            if(!globalsObject.resourceManager.resourceExists("palette_" + palette)) {
                PalettenLoader.registerResourceForPalette(palette);
            }
            globalsObject.resourceManager.publishResource("palette_" + palette, palettenCluster[palette]);
            globalsObject.resourceManager.appendStatus("palette_" + palette, "loaded");
        }
    }

    static getFlattenedPalettenCluster(palettenClusterName:string) {
        const palettenClusterStore = usePalettenClusterStore();
        const palettenCluster = palettenClusterStore.getPalettenCluster(palettenClusterName);
        if(palettenCluster === null) {
            return null;
        }

        let out=[] as PalettenEintragFromDB[];
        for(const palette in palettenCluster) {
            out = out.concat(palettenCluster[palette]);
        }
        return out;
    }
    public static getPalettenClusterResourceName = (clusterName:string)=> ["palettenCluster", clusterName].join("_");
    public static getPalettenClusterResource = (clustername:string)=>globalsObject.resourceManager.getResourceByName(PalettenLoader.getPalettenClusterResourceName(clustername));
    public static getPalettenCluster = (clustername:string) => {
        const palettenStore = usePalettenClusterStore();
        return palettenStore.getPalettenCluster(clustername);
    }
    public static loadSinglePalette(palettenName : string) {
        if(globalsObject.resourceManager.getResourceByName("palette_"+palettenName) !== null) {
            return;
        }
        globalsObject.resourceManager.registerResource({
            resourceName: "palette_"+palettenName,
            failHandler: function () {
                handleError("ajaxFail", "getOrderData",
                    "error loading palette" + palettenName);
            }
        }).load({
            url: "/konfigurator_php/designmodul/colors/loadPalette.php",
            method: "GET",
            data: {
                palette: palettenName
            },
            dataType: "json"
        });

        globalsObject.resourceManager.createTask("process_"+palettenName)
            .when("palette_"+palettenName, "loaded")
            .thenDo(function(response) {
                globalsObject.resourceManager.publishResource("palette_"+palettenName, response["palette_"+palettenName]);
            });
    }
}
